import React from 'react';
import { Package, User, DollarSign, Eye, Trash2, Tag } from 'lucide-react';
import { SlotWithReservation } from '../types';
import { SlotStatusBadge, PaymentBadge } from './SlotStatusBadge';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface SlotTableProps {
  slots: SlotWithReservation[];
  onViewDetails: (slot: SlotWithReservation) => void;
  onDelete: (slot: SlotWithReservation) => void;
  onPriceOverride?: (slot: SlotWithReservation) => void;
}

export default function SlotTable({ slots, onViewDetails, onDelete, onPriceOverride }: SlotTableProps) {
  return (
    <div className="hidden lg:block bg-white shadow-sm rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                Slot #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '12%'}}>
                Dimensions
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                Price/Day
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '12%'}}>
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '28%'}}>
                Vendor Info
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '18%'}}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {slots.map((slot) => (
              <tr key={slot.id}>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{slot.slot_number}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-gray-900 capitalize">{slot.slot_type}</span>
                  {slot.has_power && (
                    <div className="text-xs text-secondary mt-1">⚡ Power</div>
                  )}
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-gray-900">{slot.dimensions || 'N/A'}</span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(slot.base_price).replace('$', '')} {/* Issue #905: Decimal.js formatting */}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-col gap-1">
                    <SlotStatusBadge slot={slot} />
                    {slot.reservation && <PaymentBadge paymentStatus={slot.reservation.payment_status} />}
                  </div>
                </td>
                <td className="px-4 py-4">
                  {slot.is_reserved && slot.reservation ? (
                    <div className="text-sm">
                      <div className="flex items-center gap-1 font-medium text-gray-900">
                        <User className="h-3 w-3 text-gray-400" />
                        {slot.reservation.vendor_name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {slot.reservation.vendor_email}
                      </div>
                      {slot.reservation.vendor_phone && (
                        <div className="text-xs text-gray-500">
                          {slot.reservation.vendor_phone}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {formatCurrency(slot.reservation.price_paid)} • {slot.reservation.reservation_type} {/* Issue #905: Decimal.js formatting */}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">No vendor assigned</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewDetails(slot)}
                      className="p-2 text-secondary hover:bg-moonbone rounded"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {onPriceOverride && (
                      <button
                        onClick={() => onPriceOverride(slot)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                        title="Set Price Override"
                      >
                        <Tag className="h-4 w-4" />
                      </button>
                    )}
                    {!slot.is_reserved && (
                      <button
                        onClick={() => onDelete(slot)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete Slot"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
